// Learnosity's session_duration is wall-clock minutes between dt_started and dt_completed, so an
// assessment left open overnight reports hundreds of minutes. Summing learnosity_session_responses
// .duration instead reports active time, which is what a teacher means by "time spent". See
// shared/Nc.Shared/Learnosity/Api/Models/Responses/SingleSkillAssessment.cs for session_duration.
//
// Known, accepted over-count: that column holds Learnosity's ITEM-level active seconds replicated
// onto each of the item's response rows, not a true per-response figure — SkillAssessmentProcessor
// .cs:81-84 fans one item.Time across every ResponseId before writing it at line 100. A multi-part
// item therefore contributes its time once per response, so the plain sum below over-counts it by
// (responses - 1)x. This matches the existing precedent in DiagnosticStatusReportBuilder.cs:67.
// Deduplicating by itemReference would be the fix if those rows ever read high.

type AssessmentResponseLike =
  | {
      // Active seconds. Null for assessments taken before the column existed (TEAMC-450, Oct 2024)
      // and for any response Learnosity reported no time for. Zero is real data, not a gap.
      readonly duration?: number | null | undefined;
    }
  | null
  | undefined;

export interface AssessmentTimeSource {
  /** Wall-clock minutes from Learnosity. Used only as a fallback. */
  readonly sessionLength: number;
  readonly items?: ReadonlyArray<AssessmentResponseLike> | null | undefined;
}

export interface AssessmentTimeDisplay {
  /** Compact visual text, e.g. '4m' or '<1m'. */
  readonly display: string;
  /**
   * Spoken equivalent. Screen readers at default punctuation levels drop '<' entirely, which would
   * make a sub-minute assessment read identically to a one-minute one.
   */
  readonly label: string;
}

const minutesDisplay = (minutes: number): AssessmentTimeDisplay => ({
  display: `${minutes}m`,
  label: `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`,
});

/**
 * Total active seconds across an assessment's responses, or null when not a single response
 * reported a duration — which is the signal to fall back to the wall-clock sessionLength.
 */
export const sumResponseDurationSeconds = (
  items: ReadonlyArray<AssessmentResponseLike> | null | undefined,
): number | null => {
  const durations = (items ?? [])
    .map(item => item?.duration)
    .filter((duration): duration is number => duration !== null && duration !== undefined);

  if (durations.length === 0) return null;

  return durations.reduce((total, duration) => total + duration, 0);
};

/**
 * Builds the "Time" value for an assessment row.
 *
 * Assessments with no reported durations fall back to the wall-clock sessionLength, so rows
 * predating the duration column still show the number they showed before.
 *
 * Rounds to nearest rather than up: the point of using active time is to report time actually
 * spent, and rounding up would inflate every row with a partial minute. A total under 30 seconds
 * therefore rounds to zero minutes, which renders as '<1m' so a genuinely fast assessment is not
 * mistaken for one with no timing data.
 */
export const formatAssessmentTime = (assessment: AssessmentTimeSource): AssessmentTimeDisplay => {
  const totalSeconds = sumResponseDurationSeconds(assessment.items);

  if (totalSeconds === null) return minutesDisplay(assessment.sessionLength);

  const minutes = Math.round(totalSeconds / 60);

  return minutes < 1 ? { display: '<1m', label: 'less than 1 minute' } : minutesDisplay(minutes);
};
